<template>
  <hs-modal
    :model-value="true"
    :title="group.name"
    :description="group.description || 'Manage group stores, members, promo codes, and settings.'"
    size="xl"
    @update:modelValue="handleModalModel"
    @close="$emit('close')"
  >
    <div class="gdm-shell">
      <div class="gdm-summary">
        <div class="gdm-summary__mark">
          <hs-icon name="users" :size="22" />
        </div>
        <div class="gdm-summary__copy">
          <span>Group workspace</span>
          <strong>{{ group.name }}</strong>
          <p v-if="group.description">{{ group.description }}</p>
          <p v-else>Review stores, users, permissions, and collaboration rules.</p>
        </div>
        <hs-badge :tone="getRoleTone(displayRole)" icon="shield">
          {{ displayRole }}
        </hs-badge>
      </div>

      <hs-tabs :tabs="tabs" v-model="activeTab" />

      <div class="gdm-content">
        <group-overview-tab
          v-if="activeTab === 'overview'"
          :group="group"
          :store-id="storeId"
          @refresh="$emit('refresh')"
          @leave-group="handleLeaveGroup"
        />

        <group-stores-tab
          v-else-if="activeTab === 'stores'"
          :group="group"
          :store-id="storeId"
          @refresh="$emit('refresh')"
        />

        <group-members-tab
          v-else-if="activeTab === 'members'"
          :group="group"
          @refresh="$emit('refresh')"
        />

        <group-promo-codes-tab
          v-else-if="activeTab === 'promo-codes'"
          :group="group"
          @refresh="$emit('refresh')"
        />

        <group-settings-tab
          v-else-if="activeTab === 'settings'"
          :group="group"
          @refresh="$emit('refresh')"
        />
      </div>
    </div>

    <hs-confirm-dialog
      v-model="showLeaveConfirmation"
      title="Leave Group"
      :message="`Are you sure you want to leave '${group.name}'? You'll need a new invitation to rejoin.`"
      tone="danger"
      confirm-text="Leave Group"
      cancel-text="Cancel"
      :loading="isLeaving"
      @confirm="confirmLeaveGroup"
    />
  </hs-modal>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import GroupOverviewTab from "@/components/settings/group/GroupOverviewTab.vue";
import GroupStoresTab from "@/components/settings/group/GroupStoresTab.vue";
import GroupMembersTab from "@/components/settings/group/GroupMembersTab.vue";
import GroupPromoCodesTab from "@/components/settings/group/GroupPromoCodesTab.vue";
import GroupSettingsTab from "@/components/settings/group/GroupSettingsTab.vue";
import { HsBadge, HsConfirmDialog, HsIcon, HsModal, HsTabs } from "@/components/ui";

export default {
  name: "GroupDetailsModal",
  components: {
    GroupOverviewTab,
    GroupStoresTab,
    GroupMembersTab,
    GroupPromoCodesTab,
    GroupSettingsTab,
    HsBadge,
    HsConfirmDialog,
    HsIcon,
    HsModal,
    HsTabs,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    storeId: {
      type: Number,
      required: true,
    },
  },
  emits: ["close", "refresh"],
  setup(props, { emit }) {
    const activeTab = ref("overview");
    const store = useStore();
    const showLeaveConfirmation = ref(false);
    const isLeaving = ref(false);

    const userId = computed(() => store.getters.getAuthenticatedUser?.id);
    const currentUserRole = computed(
      () => store.getters["groups/currentUserRole"] || null
    );
    const isCurrentUserOwner = computed(
      () => store.getters["groups/isCurrentUserOwner"]
    );
    const currentGroupPermissions = computed(
      () => store.getters["groups/currentGroupPermissions"] || []
    );
    const canReadGroup = computed(() => {
      return (
        isCurrentUserOwner.value ||
        currentGroupPermissions.value.includes("CAN_READ")
      );
    });
    const canReadPromoCodes = computed(() => {
      return (
        store.getters["groups/canReadPromoCodes"] ||
        isCurrentUserOwner.value
      );
    });
    const canManageSettings = computed(() => {
      return (
        store.getters["groups/canManageSettings"] ||
        isCurrentUserOwner.value
      );
    });
    const displayRole = computed(() => {
      return currentUserRole.value || props.group.membership?.role || "MEMBER";
    });

    onMounted(async () => {
      // Set current group and load permissions when modal opens
      if (userId.value) {
        await store.dispatch("groups/setCurrentGroup", {
          group: props.group,
          userId: userId.value,
        });
      }
    });

    onUnmounted(() => {
      store.dispatch("groups/clearCurrentGroup");
    });

    const tabs = computed(() => {
      const visibleTabs = [{ id: "overview", label: "Overview", icon: "layout" }];

      if (canReadGroup.value) {
        visibleTabs.push(
          { id: "stores", label: "Stores", icon: "shopping-bag" },
          { id: "members", label: "Members", icon: "users" }
        );
      }

      if (canReadPromoCodes.value) {
        visibleTabs.push({ id: "promo-codes", label: "Promo Codes", icon: "tag" });
      }

      if (canManageSettings.value) {
        visibleTabs.push({ id: "settings", label: "Settings", icon: "settings" });
      }

      return visibleTabs;
    });

    watch(tabs, (nextTabs) => {
      if (!nextTabs.some((tab) => tab.id === activeTab.value)) {
        activeTab.value = nextTabs[0]?.id || "overview";
      }
    });

    const getRoleTone = (role) => {
      const tones = {
        OWNER: "warning",
        ADMIN: "primary",
        MEMBER: "success",
        READ_ONLY: "neutral",
      };
      return tones[role] || "neutral";
    };

    const handleModalModel = (open) => {
      if (!open) {
        emit("close");
      }
    };

    const handleLeaveGroup = () => {
      // Show confirmation modal
      showLeaveConfirmation.value = true;
    };

    const confirmLeaveGroup = async () => {
      isLeaving.value = true;
      try {
        await store.dispatch("groups/leaveGroup", {
          groupId: props.group.id,
          storeId: props.storeId,
        });
        toast.success("Left group successfully");
        showLeaveConfirmation.value = false;
        emit("close");
        emit("refresh");
      } catch (err) {
        console.error("Failed to leave group", err);
        toast.error(
          err?.response?.data?.error?.message || "Unable to leave group."
        );
      } finally {
        isLeaving.value = false;
      }
    };

    return {
      activeTab,
      tabs,
      displayRole,
      getRoleTone,
      handleModalModel,
      handleLeaveGroup,
      showLeaveConfirmation,
      isLeaving,
      confirmLeaveGroup,
    };
  },
};
</script>

<style scoped>
.gdm-shell {
  display: grid;
  gap: 1rem;
}

.gdm-summary {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  gap: 0.9rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 1rem;
}

.gdm-summary__mark {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
  border-radius: 12px;
  color: var(--hs-primary, #3293ec);
  display: inline-flex;
  height: 2.7rem;
  justify-content: center;
  width: 2.7rem;
}

.gdm-summary__copy {
  min-width: 0;
}

.gdm-summary__copy span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gdm-summary__copy strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 1rem;
  font-weight: 950;
  margin-top: 0.2rem;
}

.gdm-summary__copy p {
  color: var(--hs-muted, #64748b);
  font-size: 0.83rem;
  font-weight: 700;
  margin: 0.2rem 0 0;
}

.gdm-content {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 42%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

@media (max-width: 640px) {
  .gdm-summary {
    align-items: flex-start;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .gdm-summary :deep(.ui-badge) {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .gdm-content {
    padding: 0.75rem;
  }
}
</style>
